import React, { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useItem, useItemReturnReasons, useItemWarehousePicking } from '@/hooks/item';
import { ItemRelistWarehouseModal } from '@/types/modal';
import { DatePicker, Dropdown, Input } from '@/components/molecules/common/Form';
import { CopyModal, ErrorModal, LoadingModal, StandardModal } from '@/components/molecules/common/Modal';
import styles from './Modal.module.scss';

const View: React.FC<{ modal: ItemRelistWarehouseModal }> = ({ modal }) => {
  const { t } = useTranslation();
  const { item, onClose } = modal;

  const currentTime = dayjs(new Date());
  const [dueDate, setDueDate] = useState<dayjs.Dayjs | null>(dayjs());
  const [reason, setReason] = useState('');
  const [comment, setComment] = useState('');
  const itemR = useItem(item.productId, false);
  const submitR = useItemWarehousePicking(item.productId);
  const reasonsR = useItemReturnReasons('relist');

  useEffect(() => {
    if (submitR.kind === 'Completed') {
      itemR.refetch();
    }
  }, [submitR.kind]);

  useEffect(() => {
    if (reasonsR.kind === 'Loaded' && reasonsR.data.data.length > 0) {
      setReason(String(reasonsR.data.data[0].id));
    }
  }, [reasonsR.kind]);

  if (reasonsR.kind === 'Called') {
    return (
      <LoadingModal
        title={t('modal.item_relist_warehouse.title')}
        onClose={onClose}
      />
    );
  } else if (reasonsR.kind === 'Error') {
    return (
      <ErrorModal
        title={t('modal.item_relist_warehouse.title')}
        message={t('modal.item_relist_warehouse.reasons_error')}
        onClose={onClose}
      />
    );
  } else if (submitR.kind === 'Error') {
    return (
      <ErrorModal
        title={t('modal.item_relist_warehouse.title')}
        error={submitR.error}
        onClose={onClose}
      />
    );
  } else if (submitR.kind === 'Completed') {
    return (
      <CopyModal
        title={t('modal.item_relist_warehouse.picking_reference_number')}
        beforeInfo={
          <div className={styles.paddingY1rem}>{t('modal.item_relist_warehouse.picking_reference_generated')}</div>
        }
        afterInfo={<br />}
        textToCopy={String(submitR.data.data.pickingId)}
        onClose={onClose}
      />
    );
  }

  const reasons = reasonsR.data.data.map(({ id, name }) => ({ label: name, value: String(id) }));
  const body = (
    <div className={styles.containerGap}>
      <div className={styles.text1rem}>{`${t('modal.item_relist_warehouse.item')} #${item.productId}`}</div>
      <Dropdown
        required={true}
        disabled={false}
        label={t('modal.item_relist_warehouse.request_reason')}
        value={reason}
        onSelect={setReason}
        items={reasons}
      />
      <DatePicker
        required={true}
        disabled={false}
        label={t('modal.item_relist_warehouse.return_date')}
        min={currentTime}
        onChange={setDueDate}
        value={dueDate || undefined}
      />
      <Input
        disabled={false}
        label={t('modal.item_relist_warehouse.request_details')}
        value={comment}
        multiline={true}
        rows={4}
        onChange={setComment}
      />
    </div>
  );

  return (
    <StandardModal
      title={t('modal.item_relist_warehouse.title')}
      onClose={onClose}
      onSubmit={() =>
        submitR.mutate({
          dueDate: dayjs(dueDate).format('YYYY-MM-DD'),
          reasonId: Number(reason),
          comment,
          isReturnToSeller: false,
        })
      }
      submitLabel={t('common.submit').toUpperCase()}
      submittable={submitR.kind !== 'Loading' && dueDate != null && reason != ''}
      body={body}
      containerStyle={{ width: 500 }}
      modalName="item_relist_warehouse"
    />
  );
};

export default View;
